import type { BuiltinIntegrationSpec } from '@repo/common/interfaces/integrationSpec';
import { isNil } from 'lodash-es';
import type { FeatureFlags } from '../../../utils/featureFlags';
import { checkFeatureFlag } from '../../../utils/featureFlags/featureFlagUtils';

export interface IntegrationLimitCheck {
	showIntegrationLimitReachedModal: boolean;
	showExtensionLimitReachedModal: boolean;
}

export interface IntegrationLimitCheckProps {
	newIntegrationInterfaceType: 'builtin' | 'marketplace';
	newIntegrationType?: BuiltinIntegrationSpec['type'];
	integrations?: { results: Array<{ type: string }> };
	trial: boolean;
	usedIntegrationSeats: number;
	integrationSeats: number;
	usedExtensionSeats: number;
	extensionSeats: number;
	trialIntegrationsLimit?: number | null;
	trialExtensionsLimit?: number | null;
}

export function checkIntegrationLimits({
	newIntegrationInterfaceType,
	newIntegrationType,
	integrations,
	trial,
	usedIntegrationSeats,
	integrationSeats,
	usedExtensionSeats,
	extensionSeats,
	trialIntegrationsLimit,
	trialExtensionsLimit,
}: IntegrationLimitCheckProps): IntegrationLimitCheck {
	const paidIntegrationLimitReached =
		usedIntegrationSeats >= integrationSeats &&
		// Customers can add additional integrations of the same type, but not for a trial
		!integrations?.results.some(
			(integration) =>
				newIntegrationInterfaceType === 'builtin' &&
				integration.type === newIntegrationType
		);

	const paidExtensionLimitReached =
		usedExtensionSeats >= extensionSeats &&
		// Customers can add additional extensions of the same type, but not for a trial
		!integrations?.results.some(
			(integration) =>
				newIntegrationInterfaceType === 'builtin' &&
				integration.type === newIntegrationType
		);

	const trialIntegrationLimitReached =
		trial &&
		!isNil(trialIntegrationsLimit) &&
		usedIntegrationSeats >= trialIntegrationsLimit;

	const trialExtensionsLimitReached =
		trial &&
		!isNil(trialExtensionsLimit) &&
		usedExtensionSeats >= trialExtensionsLimit;

	return {
		showIntegrationLimitReachedModal:
			paidIntegrationLimitReached || trialIntegrationLimitReached,
		showExtensionLimitReachedModal:
			paidExtensionLimitReached || trialExtensionsLimitReached,
	};
}

export interface ShouldShowIntegrationWithFeatureFlagProps {
	integration: BuiltinIntegrationSpec;
	featureFlags: FeatureFlags;
	existingIntegrations?: { results: Array<{ type: string }> };
}

/**
 * Determines if an integration should be shown based on its feature flag.
 * If the feature flag is disabled, the integration will still be shown
 * if the user has an existing integration of that type.
 */
export function shouldShowIntegrationWithFeatureFlag({
	integration,
	featureFlags,
	existingIntegrations,
}: ShouldShowIntegrationWithFeatureFlagProps): boolean {
	if (!integration.featureFlag) {
		return true; // No feature flag = always show
	}

	const flagEnabled = checkFeatureFlag(integration.featureFlag, featureFlags);

	// If feature flag is enabled, show the integration
	if (flagEnabled) {
		return true;
	}

	// If feature flag is disabled, check if user has an existing integration of this type
	const hasExistingIntegration = existingIntegrations?.results.some(
		(existingIntegration) => existingIntegration.type === integration.type
	);

	return hasExistingIntegration ?? false;
}
